import ANALYTIC_EVENTS from "@/feature-analytics/analytic-events.mjs";
import { refs__PRIVATE as refs } from "@/feature-analytics/tracker.mjs";
import { devDebug, devError, devWarn } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";

const SAMPLE_RATE = 0.02;

const PERF_NAV_TYPE = "navigation" as const;
const PERF_LCP_TYPE = "largest-contentful-paint" as const;
const PERF_LSH_TYPE = "layout-shift" as const;

const POST_RENDER_COLLECTION_TIME = 2;
const CANCEL_ON_EVENTS = [
  "mousedown",
  "pointerdown",
  "keydown",
  "touchstart",
  "unload",
];

let observer: PerformanceObserver | null = null;

interface NavigationPerformanceResults {
  page_load_time: number;
  page_download_time: number;
  dns_time: number;
  redirect_response_time: number;
  server_response_time: number;
  tcp_connect_time: number;
  dom_interactive_time: number;
  content_load_time: number;
}

interface LayoutPerformanceResults {
  largest_contentful_paint: number;
  cumulative_layout_shift: number;
}

type PerformanceResults = Partial<
  NavigationPerformanceResults & LayoutPerformanceResults
>;

let results: PerformanceResults | null = null;

function startPerformanceEvaluation() {
  if (observer) {
    devWarn("[perf] restarting performance evaluation");

    stopPerformanceEvaluation();
  }

  results = {};

  const pt = globals.performance;

  if (!pt || !pt.getEntriesByType) return;

  const supportedEntryTypes = PerformanceObserver?.supportedEntryTypes;

  if (!supportedEntryTypes) return;

  if (supportedEntryTypes.includes(PERF_NAV_TYPE)) {
    const navigationTiming = pt.getEntriesByType(
      PERF_NAV_TYPE,
    )[0] as PerformanceNavigationTiming;

    if (!navigationTiming) return;

    const navigationResults: NavigationPerformanceResults = {
      page_load_time:
        navigationTiming.loadEventStart - navigationTiming.startTime,
      page_download_time:
        navigationTiming.responseEnd - navigationTiming.responseStart,
      dns_time:
        navigationTiming.domainLookupEnd - navigationTiming.domainLookupStart,
      redirect_response_time:
        navigationTiming.redirectEnd - navigationTiming.redirectStart,
      server_response_time:
        navigationTiming.responseStart - navigationTiming.requestStart,
      tcp_connect_time:
        navigationTiming.connectEnd - navigationTiming.connectStart,
      dom_interactive_time:
        navigationTiming.domInteractive - navigationTiming.startTime,
      content_load_time:
        navigationTiming.domContentLoadedEventStart -
        navigationTiming.startTime,
    };

    for (const [key, value] of Object.entries(navigationResults)) {
      if (value) {
        results[key] = Math.round(value) / 1000;
      }
    }
  }

  const performanceObserverEvents = [
    supportedEntryTypes.includes(PERF_LCP_TYPE) && PERF_LCP_TYPE,
    supportedEntryTypes.includes(PERF_LSH_TYPE) && PERF_LSH_TYPE,
  ].filter(Boolean);

  if (performanceObserverEvents.length === 0) {
    submitResults();

    return;
  }

  devDebug("[perf] starting performance evaluation");

  observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (!results) {
        devError("[perf] unable to record performance results");

        return;
      }

      if (entry.entryType === PERF_LCP_TYPE) {
        results.largest_contentful_paint = entry.startTime / 1000 || 0;
      }

      if (isLayoutShiftEvent(entry)) {
        results.cumulative_layout_shift =
          (results.cumulative_layout_shift ?? 0) + (entry.value || 0);
      }
    });
  });

  observer.observe({
    entryTypes: performanceObserverEvents,
  });
}

function isLayoutShiftEvent(
  entry: PerformanceEntry,
): entry is PerformanceEntry & { value?: number } {
  return entry.entryType === PERF_LSH_TYPE;
}

function stopPerformanceEvaluation() {
  if (!observer) return;

  for (const eventId of CANCEL_ON_EVENTS) {
    globals.removeEventListener(eventId, stopPerformanceEvaluation);
  }

  observer.disconnect();
  observer = null;

  devDebug("[perf] performance evaluation stopped");

  submitResults();
}

function submitResults() {
  if (!results) return;

  if (typeof results.cumulative_layout_shift === "number") {
    results.cumulative_layout_shift =
      Math.round(results.cumulative_layout_shift * 1000) / 1000;
  }

  devDebug("[perf] submitting results", results);

  refs.trackEvent(ANALYTIC_EVENTS.PERFORMANCE_TIMING, results);

  results = null;
}

export function onAppRendered() {
  if (!observer) return;

  // Delay submitting performance data to capture delayed repaints, unless the user interacted with the page
  setTimeout(stopPerformanceEvaluation, POST_RENDER_COLLECTION_TIME * 1000);

  for (const eventId of CANCEL_ON_EVENTS) {
    globals.addEventListener(eventId, stopPerformanceEvaluation);
  }
}

if (Math.random() < SAMPLE_RATE) {
  startPerformanceEvaluation();
}
